import {
  AdminPaintSchemeApiRow,
  AdminPaintSchemeFormValue,
  AdminPaintSchemeGridRow,
  AdminPaintSchemeRecord,
  PAINT_PERFORMANCE_INDEXES,
  PaintPerformanceIndex,
} from "../models/admin-paint-scheme.models";

const performanceIndexSet = new Set<string>(PAINT_PERFORMANCE_INDEXES);

export function normalizeAdminPaintSchemeRecord(
  value: AdminPaintSchemeApiRow,
  rowNumber?: number,
): AdminPaintSchemeRecord {
  const rowPrefix = rowNumber === undefined ? "" : `Row ${rowNumber}: `;
  const vendor = String(value.VENDOR ?? "").trim();
  const paint = String(value.PAINT ?? "").trim();
  const performanceIndex = String(value.PERFORMANCE_INDEX ?? "Unknown").trim() || "Unknown";
  const rawTier = typeof value.HEMPEL_TIER === "string" ? value.HEMPEL_TIER.trim() : value.HEMPEL_TIER;
  const hempelTier = rawTier === "" || rawTier == null ? Number.NaN : Number(rawTier);

  if (!vendor) {
    throw new Error(`${rowPrefix}Vendor is required.`);
  }
  if (!paint) {
    throw new Error(`${rowPrefix}Paint is required.`);
  }
  if (!performanceIndexSet.has(performanceIndex)) {
    throw new Error(`${rowPrefix}Performance index must be ${PAINT_PERFORMANCE_INDEXES.join(", ")}.`);
  }
  if (!Number.isInteger(hempelTier) || hempelTier < 0 || hempelTier > 5) {
    throw new Error(`${rowPrefix}Hempel tier must be a whole number from 0 to 5.`);
  }

  const comments = String(value.COMMENTS ?? "").trim();

  return {
    VENDOR: vendor,
    PAINT: paint,
    PERFORMANCE_INDEX: performanceIndex as PaintPerformanceIndex,
    HEMPEL_TIER: hempelTier,
    COMMENTS: comments || null,
    UPLOAD_TS: String(value.UPLOAD_TS ?? "").trim(),
  };
}

export function normalizeAdminPaintSchemeRows(rows: AdminPaintSchemeApiRow[]): AdminPaintSchemeRecord[] {
  return rows.map((row, index) => normalizeAdminPaintSchemeRecord(row, index + 1));
}

export function toAdminPaintSchemeGridRows(rows: AdminPaintSchemeApiRow[]): AdminPaintSchemeGridRow[] {
  return rows.map((row, index) => ({
    gridRowId: createGridRowId(row, index),
    VENDOR: String(row.VENDOR ?? "").trim(),
    PAINT: String(row.PAINT ?? "").trim(),
    PERFORMANCE_INDEX: toDisplayPerformanceIndex(row.PERFORMANCE_INDEX),
    HEMPEL_TIER: toDisplayHempelTier(row.HEMPEL_TIER),
    COMMENTS: String(row.COMMENTS ?? "").trim() || null,
    UPLOAD_TS: String(row.UPLOAD_TS ?? "").trim(),
  }));
}

export function toAdminPaintSchemeRecordFromForm(value: AdminPaintSchemeFormValue): AdminPaintSchemeRecord {
  return normalizeAdminPaintSchemeRecord({
    VENDOR: value.vendor,
    PAINT: value.paint,
    PERFORMANCE_INDEX: value.performanceIndex,
    HEMPEL_TIER: value.hempelTier,
    COMMENTS: value.comments,
    UPLOAD_TS: "",
  });
}

export function toAdminPaintSchemeFormValue(row: AdminPaintSchemeApiRow): AdminPaintSchemeFormValue {
  return {
    vendor: String(row.VENDOR ?? "").trim(),
    paint: String(row.PAINT ?? "").trim(),
    performanceIndex: toDisplayPerformanceIndex(row.PERFORMANCE_INDEX),
    hempelTier: String(toDisplayHempelTier(row.HEMPEL_TIER)),
    comments: String(row.COMMENTS ?? "").trim(),
  };
}

export function getAdminPaintSchemeDisplayLabel(row: AdminPaintSchemeApiRow): string {
  const vendor = String(row.VENDOR ?? "").trim() || "Unnamed vendor";
  const paint = String(row.PAINT ?? "").trim() || "Unnamed paint";
  return `${vendor} | ${paint}`;
}

export function formatAdminPaintSchemeUploadTimestamp(value: unknown): string {
  const text = String(value ?? "").trim();
  if (!text) return "—";
  const parsed = new Date(text);
  return Number.isNaN(parsed.getTime())
    ? text
    : parsed.toLocaleString(undefined, { timeZone: "UTC", timeZoneName: "short" });
}

export function createAdminPaintSchemeCsv(rows: AdminPaintSchemeGridRow[]): string {
  const header = ["Vendor", "Paint", "Performance Index", "Hempel Tier", "Comments", "Uploaded"];
  const records = rows.map((row) => [
    row.VENDOR,
    row.PAINT,
    row.PERFORMANCE_INDEX,
    row.HEMPEL_TIER,
    row.COMMENTS ?? "",
    row.UPLOAD_TS,
  ]);

  return [header, ...records].map((record) => record.map(toCsvCell).join(",")).join(String.fromCharCode(13, 10));
}

function toCsvCell(value: unknown): string {
  const text = String(value ?? "");
  const spreadsheetSafeText = /^[=+@-]/.test(text) ? "'" + text : text;
  return '"' + spreadsheetSafeText.replace(/"/g, '""') + '"';
}

function createGridRowId(row: AdminPaintSchemeApiRow, index: number): string {
  const vendor = encodeURIComponent(String(row.VENDOR ?? "").trim());
  const paint = encodeURIComponent(String(row.PAINT ?? "").trim());
  return `paint-${vendor}-${paint}-${index}`;
}

function toDisplayPerformanceIndex(value: unknown): PaintPerformanceIndex {
  const normalized = String(value ?? "Unknown").trim();
  return performanceIndexSet.has(normalized) ? (normalized as PaintPerformanceIndex) : "Unknown";
}

function toDisplayHempelTier(value: unknown): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}
